import React from 'react';
import { connect } from 'react-redux';

import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { RadioGroup, RadioGroupItem } from '@buildoutinc/blueprint-react/ui/RadioGroup';
import { Checkbox } from 'components/ui/checkbox';

import { setFormData } from '../store/actions';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { type DocumentDefaultsType, type Branding } from '../types';

interface BrandingSelectorProps {
  availableBrandings: Branding[];
  brandingIds: number[];
  defaultBrandingId: number | null;
  setFormData: (data: Partial<DocumentDefaultsType>) => void;
}

function BrandingSelector({
  availableBrandings,
  brandingIds,
  defaultBrandingId,
  setFormData,
}: BrandingSelectorProps) {
  const [selectAll, setSelectAll] = React.useState(brandingIds.length === 0);
  const handleToggle = (id: number, checked: boolean) => {
    let nextBrandingIds: number[];
    let nextDefault: number | null = defaultBrandingId;

    if (checked) {
      nextBrandingIds = [...brandingIds, id];
      if (nextDefault == null) nextDefault = id;
    } else {
      nextBrandingIds = brandingIds.filter(brandingId => brandingId !== id);
      if (nextDefault === id) nextDefault = nextBrandingIds[0] ?? null;
    }    

    setFormData({ brandingIds: nextBrandingIds, defaultBrandingId: nextDefault });
  };

  const handleDefaultChange = (value: unknown) => {
    if (typeof value !== 'string' || value === '') return;
    const id = Number(value);
    if (Number.isNaN(id)) return;
    setFormData({ defaultBrandingId: id });
  };

  if (availableBrandings.length === 0) {
    return <p className="text-muted mb-0">No brandings available to choose from.</p>;
  }

  const handleSelectAll = () => {
    setSelectAll(true);
    setFormData({ brandingIds: [], defaultBrandingId: null });
  };

  const handleOverride = () => {
    setSelectAll(false);
    const firstBrandingId = availableBrandings[0].id;
    setFormData({
      brandingIds: availableBrandings.map(b => b.id),
      defaultBrandingId: firstBrandingId
    });
  };

  const brandingLabel = (branding: Branding, index: number) => {
    if (branding.name) return branding.name;
    if (branding.primary) return `Primary Branding`;

    return `Branding ${index + 1}`;
  };

  const renderBrandingSelectors = () => {
    return availableBrandings.map((branding, idx) => {
      const isSelected = brandingIds.includes(branding.id);
      const checkboxId = `branding-${branding.id}`;
      const radioId = `branding-default-${branding.id}`;
      const label = brandingLabel(branding, idx);

      return (
        <div className="d-flex align-items-center gap-4" key={branding.id}>
          <Checkbox
            checked={isSelected}
            id={checkboxId}
            label={label}
            onCheckedChange={checked => handleToggle(branding.id, checked === true)}
          />
          {isSelected && (
            <div className="d-flex align-items-center gap-1">
              <RadioGroupItem id={radioId} value={String(branding.id)} />
              <Label className="text-muted small mb-0" htmlFor={radioId}>
                Default
              </Label>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <RadioGroup
      value={defaultBrandingId != null ? String(defaultBrandingId) : ''}
      onValueChange={handleDefaultChange}
    >
      
      <div className="d-flex flex-column gap-2">
        <ToggleOverrideSection
          checked={selectAll}
          className="border-bottom pb-3"
          name="page-option-override"
          subtitle={selectAll ?
            'Unchecking will allow you to select which brands are available in this document' :
            'Checking this will include all available brands in this document'
          }
          title="Enable all available branding within this context"
          onOverride={handleOverride}
          onRemove={handleSelectAll}
        />
        {!selectAll && renderBrandingSelectors()}
      </div>
    </RadioGroup>
  );
}

const mapStateToProps = ({ formData }: { formData: DocumentDefaultsType }) => ({
  availableBrandings: formData.availableBrandings ?? [],
  brandingIds: formData.brandingIds ?? [],
  defaultBrandingId: formData.defaultBrandingId ?? null,
});

const mapDispatchToProps = { setFormData };

export default connect(mapStateToProps, mapDispatchToProps)(BrandingSelector);
